import React, { memo, useEffect, useReducer, useRef } from "react";
import { useSelector } from "react-redux";
import Modal from "../../../components/Modal/Modal";
import ReCAPTCHAForm from "../../../components/ReCaptcha/ReCaptcha";
import { orderMethods } from "../../../shared/constants/order";
import useDeliveryConfirmation from "./hooks/useDeliveryConfirmation";
import usePickupConfirmation from "./hooks/usePickupConfirmation";
import useSearchStores from "./hooks/useSearchStores";
import PickupLocationSearch from "./PickupLocationSearch";
import { getDeliveryAddress } from "./utils/addressStorage";

const ToggleDeliveryPickup = memo(() => {
  const fetchedNearby = useRef(false);
  const searchStores = useSearchStores();
  const pickupConfirmation = usePickupConfirmation();
  const deliveryConfirmation = useDeliveryConfirmation();
  const userAddress = useSelector(state => state.user.userAddress);
  const userOrderMethod = useSelector(state => state.user.userOrderMethod);
  const userCartId = useSelector(state => state.user.userCartId);

  const [state, setState] = useReducer(
    (currentState, partialState) => ({
      ...currentState,
      ...partialState
    }),
    {
      show: false,
      display: "confirming", // "confirming", "searching"
      selectedStore: null
    }
  );

  const isPickup = userOrderMethod === orderMethods.pickup;
  const currentStore = state.selectedStore || searchStores.stores?.[0];
  const handleConfirmPickup = () => {
    if (
      !currentStore ||
      pickupConfirmation.pending ||
      searchStores.loading
    ) {
      return;
    }
    setState({ show: false });
    pickupConfirmation.mutate(currentStore);
  };
  useEffect(() => {
    if (state.show && userAddress && !fetchedNearby.current) {
      searchStores.fetch(userAddress);
      fetchedNearby.current = true;
    }
  }, [state.show, userAddress, searchStores.fetch]);

  // Return null if we're in pickup mode and there's no saved delivery address
  if (isPickup && !getDeliveryAddress(userCartId)) {
    return null;
  }

  return (
    <>
      <div className="flex justify-around md:justify-start md:gap-4">
        <button
          type="button"
          data-testid="toggle-delivery-button"
          className={`${
            isPickup
              ? "border-dark text-dark border-2 bg-white"
              : "bg-dark text-white"
          } py-2 px-6 rounded-full h-auto w-auto ${
            isPickup ? "border-solid" : ""
          }`}
          onClick={isPickup ? () => deliveryConfirmation.mutate() : undefined}
          tabIndex={0}
          onKeyDown={e => {
            if (e.key === "Enter" && isPickup) {
              deliveryConfirmation.mutate();
            }
          }}
        >
          Delivery
        </button>
        <button
          type="button"
          data-testid="toggle-pickup-button"
          className={`${
            isPickup
              ? "bg-dark text-white"
              : "border-dark text-dark border-2 bg-white"
          } py-2 px-6 rounded-full h-auto w-auto ${
            isPickup ? "" : "border-solid"
          }`}
          onClick={isPickup ? undefined : () => setState({ show: true })}
          tabIndex={0}
          onKeyDown={e => {
            if (e.key === "Enter" && !isPickup) {
              setState({ show: true });
            }
          }}
        >
          Pickup
        </button>
      </div>

      {state.show && (
        <Modal>
          <div
            className="p-[20px] w-full text-dark relative overflow-hidden"
            role="dialog"
          >
            {state.display === "confirming" && (
              <div className="flex flex-col gap-8">
                <h4 className="font-congenialBlack text-xl text-center">
                  Confirm Pickup Bakery
                </h4>
                <button
                  type="button"
                  tabIndex={0}
                  onClick={() => setState({ show: false })}
                  onKeyDown={e => {
                    if (e.key === "Enter") {
                      setState({ show: false });
                    }
                  }}
                  className="absolute top-4 right-4 bg-transparent p-0 m-0 w-auto h-auto font-sans"
                >
                  X
                </button>
                {searchStores.showCaptchaChallenge && (
                  <div className="text-center">
                    <ReCAPTCHAForm setToken={searchStores.onCaptchaVerify} />
                  </div>
                )}
                {!searchStores.showCaptchaChallenge &&
                  currentStore !== undefined && (
                    <div className="text-center">
                      <p className="font-congenialBlack text-xl">
                        {currentStore.name}
                      </p>
                      <p>{currentStore.mailingAddress}</p>
                      <p>{`${currentStore.city}, ${currentStore.state}`}</p>
                    </div>
                  )}
                <div className="flex gap-2 items-center justify-end">
                  <button
                    type="button"
                    data-testid="change-pickup-button"
                    className="h-auto w-auto px-4 py-2 rounded-full bg-transparent"
                    disabled={
                      pickupConfirmation.pending || searchStores.loading
                    }
                    tabIndex={0}
                    onClick={() => setState({ display: "searching" })}
                    onKeyDown={e => {
                      if (e.key === "Enter") {
                        setState({ display: "searching" });
                      }
                    }}
                  >
                    Change
                  </button>
                  <button
                    type="button"
                    data-testid="confirm-pickup-button"
                    className="h-auto w-auto px-4 py-2 rounded-full bg-dark text-white"
                    disabled={
                      pickupConfirmation.pending ||
                      searchStores.loading ||
                      !currentStore
                    }
                    tabIndex={0}
                    onClick={handleConfirmPickup}
                    onKeyDown={e => {
                      if (e.key === "Enter") {
                        handleConfirmPickup();
                      }
                    }}
                  >
                    Confirm
                  </button>
                </div>
              </div>
            )}
            {state.display === "searching" && (
              <PickupLocationSearch
                userAddress={userAddress}
                onClose={() => setState({ display: "confirming", show: false })}
                onSelect={store =>
                  setState({ selectedStore: store, display: "confirming" })}
              />
            )}
            {/* Loader removed - using global loader instead */}
          </div>
        </Modal>
      )}

      {/* Loader removed - using global loader instead */}
    </>
  );
});

export default ToggleDeliveryPickup;
